import { Component } from '@builder.io/sdk';

import { IconIds, iconIds } from 'molecules/Icon/types';
import imageField from 'molecules/Image/config';
import sectionFields from 'molecules/Section/config';

interface Feature {
  title: string;
  description?: string;
  hasBullet?: boolean;
}

interface CardBadge {
  text: string;
  icon?: IconIds;
}

interface FeatureCard {
  badge?: CardBadge;
  features?: Feature[];
}

const dualFeatureCardsFields = [
  imageField('image'),
  {
    name: 'leftCard',
    type: 'object',
    helperText: 'Configuration for the left card (For Developers)',
    defaultValue: {
      badge: {
        text: 'For Developers',
        icon: 'profile',
      },
      features: [
        {
          title: 'Code-first',
          description: 'Write TypeScript in your IDE with full AI support through our MCP server.',
          hasBullet: true,
        },
        {
          title: 'No low-code limitations',
          description: 'Full control and flexibility when you need it.',
          hasBullet: true,
        },
        {
          title: 'Built-in infrastructure',
          description: 'Stop maintaining OAuth flows and retry logic.',
          hasBullet: true,
        },
        {
          title: 'Local development',
          description: 'Test locally, deploy globally.',
          hasBullet: true,
        },
      ],
    },
    subFields: [
      {
        name: 'badge',
        type: 'object',
        helperText: 'Badge configuration',
        subFields: [
          { name: 'text', type: 'string', defaultValue: 'For Developers' },
          {
            name: 'icon',
            type: 'string',
            enum: iconIds,
            helperText: 'Choose an icon for the badge',
            defaultValue: 'profile',
          },
        ],
      },
      {
        name: 'features',
        type: 'list',
        helperText: 'List of features for this card',
        subFields: [
          { name: 'title', type: 'string', required: true },
          { name: 'description', type: 'string' },
          { name: 'hasBullet', type: 'boolean', defaultValue: true },
        ],
      },
    ],
  },
  {
    name: 'rightCard',
    type: 'object',
    helperText: 'Configuration for the right card (For Non-Developers)',
    defaultValue: {
      badge: {
        text: 'For Non-Developers',
        icon: 'profile',
      },
      features: [
        {
          title: 'Low-code designer',
          description: 'Non-devs can build and modify integrations.',
          hasBullet: true,
        },
        {
          title: 'Self-serve deployment',
          description: 'Customer Success deploys without engineering.',
          hasBullet: true,
        },
        {
          title: 'Embedded workflow builder',
          description: 'Let customers create their own workflows.',
          hasBullet: true,
        },
        {
          title: 'Visual monitoring',
          description: "See what's happening without reading logs.",
          hasBullet: true,
        },
      ],
    },
    subFields: [
      {
        name: 'badge',
        type: 'object',
        helperText: 'Badge configuration',
        subFields: [
          { name: 'text', type: 'string', defaultValue: 'For Non-Developers' },
          {
            name: 'icon',
            type: 'string',
            enum: iconIds,
            helperText: 'Choose an icon for the badge',
            defaultValue: 'profile',
          },
        ],
      },
      {
        name: 'features',
        type: 'list',
        helperText: 'List of features for this card',
        subFields: [
          { name: 'title', type: 'string', required: true },
          { name: 'description', type: 'string' },
          { name: 'hasBullet', type: 'boolean', defaultValue: true },
        ],
      },
    ],
  },
  {
    name: 'compactSpacing',
    type: 'boolean',
    defaultValue: false,
    helperText:
      'Tightens the vertical spacing between checklist items. Turn on when items are a single line of copy with no description.',
  },
  ...sectionFields,
] as const;

const dualFeatureCardsConfig = {
  name: 'DualFeatureCards',
  inputs: dualFeatureCardsFields,
} as Component;

export interface DualFeatureCardsConfigProps extends GenerateBuilder<typeof dualFeatureCardsFields> {
  backgroundImage?: string;
  leftCard?: FeatureCard;
  rightCard?: FeatureCard;
}

export default dualFeatureCardsConfig;
